import {
    ensureAssociationsForWaste,
    ensureMothersOfWaste,
    ensureStampsForWaste,
    KingBasura,
    mothersOfWasteTqo,
    Waste,
    WasteAssociation,
    wasteByIdAsofTqo,
    WasteStamp,
    WasteStampFlavor,
    WasteStatusCode,
    watchAssociationsForWasteTqo,
    watchStampsForWasteTqo,
    watchWasteByIdTqo,
    watchWasteFlavorByIdTqo
} from "../rpc/webrpc-client.gen.ts";
import {Flexidate, spawnTstzSpan, stringify} from "../tstzspan.ts";
import {useActiveDaughtersOfWaste, useMothership} from "../tq/tqx.tsx";
import {z} from "zod/v4";
import {QueryClient} from "@tanstack/query-core";
import {queryOptions, skipToken} from "@tanstack/react-query";
import {InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {useRefreshableInfallibleQuery} from "../util/tqrefresh.tsx";
import {useInfallibleQuery} from "../util/useInfallibleQuery.tsx";
import {errorize} from "../bomb/errorize.tsx";
import {InspectionStage} from "../inspect/stages.tsx";

export const DEFAULT_PACKING_MODE_CODE = "01";
export const FURECON_PACKING_MODE_CODE = "07";

export const WasteStatus = z.object({
    code: WasteStatusCode,
    attachment: z.optional(WasteAssociation),
    lastFlavoredStamp: z.optional(WasteStamp),
    inspectionStage: InspectionStage,
});

export type WasteStatus = z.infer<typeof WasteStatus>;

export const unknownWasteStatus: WasteStatus = {
    code: WasteStatusCode.enum.UNKNOWN,
    inspectionStage: "NOTHING",
} as const;

function assembleWasteStatus({stamps, mothers, attachment}: {
    stamps: WasteStamp[],
    attachment?: WasteAssociation,
    mothers?: Waste[],
}): WasteStatus {
    if (stamps.length < 1) {
        return {
            code: WasteStatusCode.enum.AWAITING_PICKUP,
            attachment,
            inspectionStage: "NOTHING",
        };
    }

    let code: WasteStatusCode;
    const inspectionStage: InspectionStage
        = stamps.some(maybe => maybe.flavor === "FINAL_INSPECT")
        ? "FINAL"
        : stamps.some(maybe => maybe.flavor === "PRELIM_INSPECT")
            ? "PRELIM" : "NOTHING";

    const lastFlavoredStamp = stamps.findLast(maybe => !!maybe.flavor);
    const lastFlavor = lastFlavoredStamp?.flavor;
    switch (lastFlavor) {
        case WasteStampFlavor.enum.FINAL_INSPECT:
            code = WasteStatusCode.enum.STORAGE;
            break;
        case WasteStampFlavor.enum.PRELIM_INSPECT:
            code = WasteStatusCode.enum.INSPECTING;
            break;
        case WasteStampFlavor.enum.ONLOADED:
            code = WasteStatusCode.enum.LOADED;
            break;
        default:
            if (mothers && mothers.length > 0) {
                code = WasteStatusCode.enum.SPLIT;
            } else {
                code = WasteStatusCode.enum.AWAITING_PICKUP;
            }
    }

    const rv: WasteStatus = {code, attachment, lastFlavoredStamp, inspectionStage};
    return rv;
}

export const wasteStatusVerb = "WasteStatus";

export function wasteStatusTqk(args?: { wasteId: string }) {
    return args ? [wasteStatusVerb, args] as const : [wasteStatusVerb] as const;
}

export function ensureWasteStatus(args: {
    tqc: QueryClient,
    mama: KingBasura,
    args: { wasteId: string }
}): Promise<WasteStatus> {
    return Promise.all([
        ensureStampsForWaste(args).then(rpc => rpc.stamps),
        ensureMothersOfWaste(args).then(rpc => rpc.mothers),
        ensureAssociationsForWaste(args).then(rpc => rpc.assoc),
    ]).then(([stamps, mothers, attachment]) => {
        const rv = assembleWasteStatus({stamps, mothers, attachment});
        return rv;
    });
}

export function wasteStatusTqo(args?: { tqc: QueryClient, mama: KingBasura, args: { wasteId: string } }) {
    return queryOptions({
        queryKey: wasteStatusTqk(args ? args.args : undefined),
        queryFn: args ? () => ensureWasteStatus(args) : skipToken,
    });
}

export function useWasteStatus(args?: { wasteId: string }): InfallibleUseResult<WasteStatus> {
    const mama = useMothership();
    const stampsTqrv = useRefreshableInfallibleQuery(watchStampsForWasteTqo(mama, args));
    const mothersTqrv = useRefreshableInfallibleQuery(mothersOfWasteTqo(mama, args));
    const attachmentTqrv = useRefreshableInfallibleQuery(watchAssociationsForWasteTqo(mama, args));

    if (stampsTqrv.isPending || mothersTqrv.isPending || attachmentTqrv.isPending) {
        return pendingUseResult;
    }

    const stamps = stampsTqrv.data;
    const mothers = mothersTqrv.data;
    const attachment = attachmentTqrv.data;
    return successUseResult(assembleWasteStatus({stamps, mothers, attachment}));
}

export function useLastFlavoredWasteStamp(args?: { wasteId: string }): InfallibleUseResult<WasteStamp | null> {
    const mama = useMothership();
    const tqrv
        = useRefreshableInfallibleQuery(watchStampsForWasteTqo(mama, args));
    if (tqrv.isPending) {
        return pendingUseResult;
    }

    const rv = tqrv.data.findLast(maybe => !!maybe.flavor) ?? null;
    return successUseResult(rv);
}

export const UseIsWasteActiveAsofArgs = z.object({
    wasteId: z.string(),
    asof: z.optional(z.nullable(Flexidate)),
});
export type UseIsWasteActiveAsofArgs = z.infer<typeof UseIsWasteActiveAsofArgs>;

export function useIsWasteActiveAsof(args?: UseIsWasteActiveAsofArgs): InfallibleUseResult<boolean> {
    const mama = useMothership();
    const tqargs = args
        ? {wasteId: args.wasteId, asof: stringify(args.asof ?? new Date())}
        : undefined;
    const revisionTqrv = useInfallibleQuery(wasteByIdAsofTqo(mama, tqargs));
    if (revisionTqrv.isPending) {
        return pendingUseResult;
    }
    const revision = revisionTqrv.data;
    return successUseResult(!!revision);
}

export const UseWasteAsofArgs = z.object({
    wasteId: z.string(),
    asof: z.optional(Flexidate),
});
export type UseWasteAsofArgs = z.infer<typeof UseWasteAsofArgs>;

export const UseWasteAsofReturn = z.object({
    waste: z.nullable(Waste),
    isActive: z.boolean(),
});
export type UseWasteAsofReturn = z.infer<typeof UseWasteAsofReturn>;

export function useWasteAsof(args ?: UseWasteAsofArgs): InfallibleUseResult<UseWasteAsofReturn> {
    const mama = useMothership();
    const tqargs = args
        ? {wasteId: args.wasteId, asof: stringify(args.asof ?? new Date())} as const
        : undefined;
    const tqrv = useInfallibleQuery(wasteByIdAsofTqo(mama, tqargs));
    if (tqrv.isPending) {
        return pendingUseResult;
    }
    let isActiveAsof = false;
    const relevantRevision = tqrv.data ?? null;
    if (relevantRevision) {
        const validSpan = spawnTstzSpan(relevantRevision.validSpan);
        const now = new Date();
        isActiveAsof = validSpan.contains(now);
    }
    return successUseResult({
        waste: relevantRevision,
        isActive: isActiveAsof,
    });
}

// function useWasteHistoryDownloadFilename({wasteId}: { wasteId: string }): LimitedUseQueryResult<string> {
//     const {data: wasteData}
//         = useQuery(lastValidWasteByIdTqo({wasteId}));
//     const {data: bundleData}
//         = useQuery(lastValidBundleForWasteTqo({wasteId}));
//     if (!wasteData || !bundleData) {
//         return MADA;
//     }
//     const parts = [
//         bundleData.bundle.displayName,
//         wasteData.waste.flavor.code,
//         wasteId,
//         format(new Date(), "yyyy.MM.dd.HH.mm.ss"),
//     ];
//     const rv = join(parts, "-") + ".json";
//     return {data: rv, isSuccess: true};
// }
//
// export function useWasteHistoryDownloadProps({wasteId}: { wasteId: string }): ActionButtonProps {
//     const {data}
//         = useRefreshableQuery(completeWasteHistoryByIdTqo({wasteId}));
//     const {t} = useTranslation();
//     const history = data?.history;
//     const {data: filename} = useWasteHistoryDownloadFilename({wasteId});
//     const perform = useCallback(() =>
//             performDownload({data: history!, filename: filename!}),
//         [history, filename]);
//
//     return {
//         color: "primary", variant: "solid",
//         disabled: !history,
//         perform,
//         children: t("downloadCompleteWasteHistory")
//     };
// }

export function useIsWasteDivisible(args ?: { wasteId: string }): InfallibleUseResult<boolean> {
    const mama = useMothership();
    const wasteTqrv
        = useRefreshableInfallibleQuery(watchWasteByIdTqo(mama, args));
    const waste = wasteTqrv.data;
    const flavorTqrv = useInfallibleQuery(watchWasteFlavorByIdTqo(mama,
        waste?.flavor ? {wasteFlavorId: waste.flavor.id} : undefined));
    const flavor = flavorTqrv.data;

    if (wasteTqrv.isPending || flavorTqrv.isPending) {
        return pendingUseResult;
    }

    const isDivisible = !!flavor?.divisible;
    return successUseResult(isDivisible);
}

type WasteDaughterField = "invV";
type DaughterWaste = Pick<Waste, WasteDaughterField>;
const wasteDaughterFields: WasteDaughterField[] = ["invV"];

function isWasteDaughterEqual(a: DaughterWaste, b: DaughterWaste) {
    for (const fn of wasteDaughterFields) {
        if (a[fn] !== b[fn]) {
            return false;
        }
    }
    return true;
}

export function useIsWasteFullyDivided(args?: { wasteId: string }): InfallibleUseResult<boolean> {
    const mama = useMothership();
    const wasteTqrv = useRefreshableInfallibleQuery(watchWasteByIdTqo(mama, args));
    const waste = wasteTqrv.data;

    const daughtersTqrv = useActiveDaughtersOfWaste(args);

    if (wasteTqrv.isPending || daughtersTqrv.isPending) {
        return pendingUseResult;
    }

    if (!!args?.wasteId && !waste) {
        throw errorize(`nonexistent waste ${args.wasteId}`);
    }

    const total = daughtersTqrv.data.reduce((acc: DaughterWaste, daughter) => {
        const rv: DaughterWaste = {};
        for (const fn of wasteDaughterFields) {
            const df = daughter[fn];
            if (df) {
                rv[fn] = (acc[fn] ?? 0) + df;
            } else {
                rv[fn] = acc[fn];
            }
        }
        return rv;
    }, {});

    const rv = !!waste && isWasteDaughterEqual(waste, total);
    return successUseResult(rv);
}

